import { useMutation } from '@tanstack/react-query'
import { useToast } from '@/shad/ui/toast/use-toast'
import { parseRequestError } from '@/app/common/utilities'
import { getAccessTokenPreview } from '@/queries/providers/requests'
import { ENV } from '@/app/common/constants'

type MarketplaceAccessMode = 'preview' | 'published'

type MarketplaceTokenRequest = {
  programId: number
  providerId: string
}

type MarketplaceTokenMutationVariables = MarketplaceTokenRequest & {
  marketplaceWindow: Window
}

interface UsePreviewTokenOptions {
  mode?: MarketplaceAccessMode
  onSuccess?: (token: string) => void
  onError?: (error: Error) => void
}

export const useGeneratePreviewToken = ({
  mode = 'preview',
  onSuccess,
  onError,
}: UsePreviewTokenOptions = {}) => {
  const { toast } = useToast()

  const mutation = useMutation({
    mutationFn: async ({
      programId,
      providerId,
    }: MarketplaceTokenMutationVariables) => {
      const { token } = await getAccessTokenPreview(programId, providerId)
      return token
    },
    onSuccess: (token, { marketplaceWindow }) => {
      const marketplacePath =
        mode === 'published' ? 'marketplace-view' : 'marketplace-preview'
      const marketplaceUrl = `${ENV.VITE_ACCESS_MARKETPLACE_PREVIEW_URL}/${marketplacePath}?token=${token}`

      if (!marketplaceWindow.closed) {
        marketplaceWindow.location.replace(marketplaceUrl)
      }

      onSuccess?.(token)
    },
    onError: (error: Error, { marketplaceWindow }) => {
      if (!marketplaceWindow.closed) {
        marketplaceWindow.close()
      }

      const errorMessage = parseRequestError(error).message
      toast({
        title: mode === 'published' ? 'View Store Failed' : 'Preview Failed',
        description: errorMessage,
        variant: 'destructive',
      })
      onError?.(error)
    },
  })

  const openMarketplace = (request: MarketplaceTokenRequest) => {
    const marketplaceWindow = window.open('', '_blank')

    if (!marketplaceWindow) {
      const error = new Error(
        'Please allow pop-ups for this site and try again.'
      )

      toast({
        title: 'Unable to Open Store',
        description: error.message,
        variant: 'destructive',
      })
      onError?.(error)
      return
    }

    marketplaceWindow.opener = null
    mutation.mutate({
      ...request,
      marketplaceWindow,
    })
  }

  return {
    ...mutation,
    openMarketplace,
  }
}
